import {
  PointAggregationResultStatus,
  TrendsQueryError,
  TrendsResult,
  TrendValue,
} from '@repo/sdk/models/components';

import { UseWebsocketQueryOptions } from '@/hooks/query-hooks/websockets';
import { GaiaQueryWithAgentId, QueryToast } from '@/queries/types';

export interface TrendsStreamQueryProps extends GaiaQueryWithAgentId {
  transform?: (data: TrendsResult | null) => TrendsRenderResult;
}

export type TrendsRenderResult = {
  interval: number;
  series: ChartSeries[];
  error?: TrendsQueryError;
};

export type ChartValue = {
  timestamp: Date;
  value: number | boolean | string | null;
  normalizedValue: number | null;
};

export type ChartSeries = {
  sourceId: string;
  values: ChartValue[];
  status: PointAggregationResultStatus;
};

const mapStatus = (status?: PointAggregationResultStatus): PointAggregationResultStatus => {
  return status === PointAggregationResultStatus.NoData
    ? PointAggregationResultStatus.NoData
    : PointAggregationResultStatus.Data;
};

export const transformTrends = (trendsResult: TrendsResult | null): TrendsRenderResult => {
  if (!trendsResult) return { interval: 0, series: [] };
  const interval = trendsResult?.metadata?.interval || 0;
  const trendValues = trendsResult?.values || {};
  const chartSeries: ChartSeries[] = [];
  Object.entries(trendValues).forEach(([sourceId, pointAggregationResult]) => {
    const aggregationResults = pointAggregationResult.results;
    const status = mapStatus(pointAggregationResult.status);

    // If no data, create an empty series entry with no_data status
    if (status === PointAggregationResultStatus.NoData) {
      chartSeries.push({
        sourceId,
        values: [],
        status,
      });
      return;
    }

    // Prioritize 'avg' over 'last' when both are available
    let selectedValues: TrendValue[];

    if (aggregationResults['avg']) {
      selectedValues = aggregationResults['avg'];
    } else if (aggregationResults['last']) {
      selectedValues = aggregationResults['last'];
    } else {
      // Fallback to first available aggregation
      const firstEntry = Object.entries(aggregationResults)[0];
      selectedValues = firstEntry ? firstEntry[1] : [];
    }

    const series: ChartSeries = {
      sourceId,
      status,
      values: selectedValues.map((result) => {
        const chartValue: ChartValue = {
          timestamp: result.timestamp,
          value: result.analogValue ?? result.booleanValue ?? result.enumValue ?? null,
          normalizedValue: result.analogNormalizedValue ?? null,
        };
        return chartValue;
      }),
    };
    chartSeries.push(series);
  });

  return { interval, series: chartSeries, error: trendsResult.error };
};

export const trendsStreamQuery = ({
  agentId,
  transform = transformTrends,
}: TrendsStreamQueryProps): UseWebsocketQueryOptions<TrendsResult | null, TrendsRenderResult> => {
  return {
    enabled: !!agentId,
    queryKey: [agentId, 'trends'],
    staleTime: Infinity,
    meta: {
      toast: {
        severity: 'off',
      } as QueryToast,
    },
    webSocketUrl: `/bff/v1/agents/${agentId}/trends-stream`,
    select: transform,
  };
};
